import React from 'react';
import loadable from '@loadable/component';
import { LoadingPage } from 'components/core/LoadingPage';
import { useTrackedDecision } from 'lib/optimizelyDebug';
import {
	OPTIMIZELY_FLAGS,
	FACILITY_PAGE_AA_TEST_VARIATIONS,
	MOBILE_STICKY_ATC_VARIATIONS,
} from 'config/optimizely';

const options = {
	fallback: <LoadingPage />,
};

const FacilityPageV2 = loadable(
	() =>
		import(
			/* webpackChunkName: "FacilityPageV2" */ '../FacilityPageV2/FacilityPageV2'
		),
	options,
);

export default function FacilityPageWrapper(props) {
	// A/A test: bucket users for reporting verification (no UI change). Placed in
	// the wrapper rather than in FacilityPageV2 so the impression is recorded on
	// page view instead of waiting on the code-split chunk to resolve.
	//
	// useTrackedDecision over useDecision so the decision also lands in
	// window.__OPTIMIZELY_DEBUG__, which is what QA reads to verify bucketing.
	const [decision] = useTrackedDecision(
		OPTIMIZELY_FLAGS.FACILITY_PAGE_AA_TEST,
	);

	// SF-22689: mobile sticky Reserve Now bar + jump nav A/B test. Read here
	// (alongside the A/A test decision, both before the lazy-loaded chunk) so
	// exposure fires on page view rather than waiting on the chunk to resolve.
	// Independent of the A/A test above - the two experiments are unrelated
	// and could run concurrently, so they're kept as separate decisions
	// rather than nested in one switch.
	//
	// Fired unconditionally, including for desktop visitors where the gated
	// feature can never render: MOBILE_STICKY_ATC's Optimizely dashboard
	// audience is confirmed scoped to platform=mobile, so desktop traffic is
	// excluded from bucketing/exposure there rather than in this code.
	const [stickyDecision] = useTrackedDecision(
		OPTIMIZELY_FLAGS.MOBILE_STICKY_ATC,
	);

	const showStickyReserveExperience =
		stickyDecision?.enabled &&
		stickyDecision?.variationKey === MOBILE_STICKY_ATC_VARIATIONS.VARIATION;

	// Both arms intentionally render identically today - this is an A/A test.
	// The branch is a scaffold so swapping in real per-arm behavior later is a
	// one-line change instead of introducing the switch from scratch.
	switch (decision?.variationKey) {
		case FACILITY_PAGE_AA_TEST_VARIATIONS.VARIATION:
		case FACILITY_PAGE_AA_TEST_VARIATIONS.CONTROL:
		default:
			return (
				<FacilityPageV2
					{...props}
					showStickyReserveExperience={showStickyReserveExperience}
				/>
			);
	}
}
